import { useState, useEffect, useMemo } from 'react';
import {
  Modal, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Button, Select, SelectItem, Input,
} from '@heroui/react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useLineItem,
  useAddLineItemHardware,
  useRemoveLineItemHardware,
  useUpdateLineItemHardware,
} from '@/api/line-items';
import { useHardwareLibrary } from '@/api/hardware-library';

interface HardwarePopupProps {
  isOpen: boolean;
  onClose: () => void;
  lineItemId: string;
}

interface HwRow {
  id?: string;          // existing LineItemHardware ID
  hardwareId: string;   // selected hardware library item
  quantity: number;
  isNew?: boolean;
  isDeleted?: boolean;
}

export default function HardwarePopup({ isOpen, onClose, lineItemId }: HardwarePopupProps) {
  const { data: lineItemRaw, isLoading } = useLineItem(isOpen ? lineItemId : null);
  const lineItem = lineItemRaw as any;
  const { data: hardwareList = [] } = useHardwareLibrary();
  const addHw = useAddLineItemHardware();
  const removeHw = useRemoveLineItemHardware();
  const updateHw = useUpdateLineItemHardware();

  const [rows, setRows] = useState<HwRow[]>([]);
  const [saving, setSaving] = useState(false);

  // Initialize rows from loaded line item
  useEffect(() => {
    if (lineItem?.hardware) {
      setRows(
        lineItem.hardware.map((h: any) => ({
          id: h.id,
          hardwareId: h.hardware_id,
          quantity: h.quantity,
        })),
      );
    }
  }, [lineItem]);

  const title = lineItem
    ? `Hardware — ${lineItem.cabinet_code || ''} ${lineItem.description}`.trim()
    : 'Hardware';

  const visibleRows = useMemo(() => rows.filter((r) => !r.isDeleted), [rows]);

  function addRow() {
    setRows((prev) => [...prev, { hardwareId: '', quantity: 1, isNew: true }]);
  }

  function removeRow(idx: number) {
    // Map visible index back to actual rows array
    let visibleIdx = 0;
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].isDeleted) continue;
      if (visibleIdx === idx) {
        setRows((prev) => {
          const copy = [...prev];
          if (copy[i].isNew) {
            // New row — just remove it
            copy.splice(i, 1);
          } else {
            // Existing row — mark for deletion
            copy[i] = { ...copy[i], isDeleted: true };
          }
          return copy;
        });
        return;
      }
      visibleIdx++;
    }
  }

  function updateRow(visibleIdx: number, field: 'hardwareId' | 'quantity', value: string | number) {
    let seen = 0;
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].isDeleted) continue;
      if (seen === visibleIdx) {
        setRows((prev) => {
          const copy = [...prev];
          copy[i] = { ...copy[i], [field]: value };
          return copy;
        });
        return;
      }
      seen++;
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const promises: Promise<any>[] = [];

      for (const row of rows) {
        if (row.isDeleted && row.id) {
          // Remove existing hardware
          promises.push(removeHw.mutateAsync({ lineItemId, hwId: row.id }));
        } else if (row.isNew && !row.isDeleted && row.hardwareId) {
          // Add new hardware
          promises.push(
            addHw.mutateAsync({ lineItemId, hardware_id: row.hardwareId, quantity: row.quantity }),
          );
        } else if (row.id && !row.isDeleted) {
          // Check if quantity changed
          const original = lineItem?.hardware?.find((h: any) => h.id === row.id);
          if (original && original.quantity !== row.quantity) {
            promises.push(
              updateHw.mutateAsync({ lineItemId, hwId: row.id, quantity: row.quantity }),
            );
          }
        }
      }

      await Promise.all(promises);
      toast.success('Hardware updated');
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update hardware');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" classNames={{ base: 'bg-[var(--bg-surface)]' }}>
      <ModalContent>
        <ModalHeader className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {title}
        </ModalHeader>
        <ModalBody>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-6 w-6 border-2 border-[var(--accent)] border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {visibleRows.length === 0 && (
                <p className="text-xs text-center py-4" style={{ color: 'var(--text-muted)' }}>
                  No hardware assigned
                </p>
              )}
              {visibleRows.map((row, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Select
                    aria-label="Hardware"
                    selectedKeys={row.hardwareId ? [row.hardwareId] : []}
                    onSelectionChange={(keys) => {
                      const val = Array.from(keys)[0] as string;
                      updateRow(idx, 'hardwareId', val);
                    }}
                    size="sm"
                    className="flex-1"
                    classNames={{ trigger: 'bg-[var(--bg-panel)] border-[var(--border)]' }}
                  >
                    {(hardwareList as any[]).map((h) => (
                      <SelectItem key={h.id}>{h.name}</SelectItem>
                    ))}
                  </Select>
                  <Input
                    aria-label="Qty"
                    type="number"
                    size="sm"
                    className="w-20"
                    min={1}
                    value={String(row.quantity)}
                    onValueChange={(v) => updateRow(idx, 'quantity', parseInt(v) || 1)}
                    classNames={{ inputWrapper: 'bg-[var(--bg-panel)] border-[var(--border)]' }}
                  />
                  <button
                    onClick={() => removeRow(idx)}
                    className="p-1 rounded hover:bg-[var(--bg-hover)]"
                    title="Remove"
                  >
                    <Trash2 size={14} style={{ color: 'var(--text-muted)' }} />
                  </button>
                </div>
              ))}
              <Button
                size="sm"
                variant="light"
                onPress={addRow}
                startContent={<Plus size={13} />}
                className="self-start"
              >
                Add Hardware
              </Button>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="light" onPress={onClose}>Cancel</Button>
          <Button
            size="sm"
            color="primary"
            onPress={handleSave}
            isLoading={saving}
            isDisabled={isLoading}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
